// The quote drawer (#1559): the Sheet shell, the close guard and the wiring
// from Next step and the ⋯ menu to each action (Approach §4–5).
import { useEffect, useId, useRef, useState, type MouseEvent } from "react";
import { toast } from "sonner";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useDuplicateQuote, useToggleLost, useUpdateQuoteStatus, type QuoteRow } from "@/hooks/useQuotes";
import { emailSwitchFrom, markSentSuccessMessage } from "@/lib/quote-sent-toast";
import { publicQuoteUrl } from "@/lib/public-urls";
import { ghlContactHref } from "@/lib/ghl-links";
import { getDocumentLabel } from "@/types/quotes";
import { useDragEscapeGuard } from "../useLineItemDrafts";
import { ActivitySection } from "./ActivitySection";
import { CrewSection } from "./CrewSection";
import { DrawerFooter } from "./DrawerFooter";
import { EventClientSection } from "./EventClientSection";
import { ItemsSection, SquareBar } from "./ItemsSection";
import { NotesSection } from "./NotesSection";
import { PaymentsSection, type PayMode } from "./PaymentsSection";
import { QuoteDrawerHeader } from "./QuoteDrawerHeader";
import { SaveTemplateDialog } from "./SaveTemplateDialog";
import { TotalsSection } from "./TotalsSection";
import { firstName, menuEntries, resolveNextStep, summarizePayments, type MenuEntryId, type NextStepId } from "./drawerLogic";
import { useEventDetailsDraft } from "./useEventDetailsDraft";
import { useBusy, useDeleteDraft, useSquareResync } from "./useQuoteActions";
import { useQuoteDrawerData } from "./useQuoteDrawerData";
import { useQuoteDrawerDraft, type QuoteDrawerDraft } from "./useQuoteDrawerDraft";

interface QuoteDrawerProps {
  quote: QuoteRow;
  onOpenChange: (open: boolean) => void;
  onContactClick?: (contactId: string) => void;
  /** After Delete draft succeeds. */
  onDeleted: () => void;
}

/** Clipboard write that reports failure instead of throwing (iOS Safari, insecure contexts). */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

const openTab = (url: string) => window.open(url, "_blank", "noopener,noreferrer");

type Command = MenuEntryId | NextStepId;

interface CommandDeps {
  quote: QuoteRow;
  draft: QuoteDrawerDraft;
  ghl: string | null;
  emailSwitch: ReturnType<typeof emailSwitchFrom>;
  onOpenChange: (open: boolean) => void;
  openTemplate: () => void;
  askDelete: () => void;
  setPayMode: (mode: PayMode) => void;
}

/** Every Next step button and ⋯ entry, wired to its existing mechanism (Approach §5). */
function useDrawerCommands({ quote, draft, ghl, emailSwitch, onOpenChange, openTemplate, askDelete, setPayMode }: CommandDeps) {
  const updateStatus = useUpdateQuoteStatus();
  const duplicate = useDuplicateQuote();
  const toggleLost = useToggleLost();
  const square = useSquareResync(quote.id);
  // Send, Mark as sent and Copy link and mark sent save first: one at a time, so a
  // double click can't save twice or send twice.
  const sending = useBusy();
  const { id } = quote;
  const url = publicQuoteUrl(id);
  // A send writes the quote's edits first, and a refused or failed save stops it.
  const savedFirst = async () => !draft.dirty || (await draft.save());
  const status = (next: string, successMessage: string, extra: { manualShare?: boolean; onlyIfUnpaid?: boolean } = {}) =>
    updateStatus.mutate({ id, status: next, successMessage, ...extra });
  const copyLink = async (message: string) => {
    if (await copyText(url)) {
      toast.success(message);
      return true;
    }
    toast(`Select and copy: ${url}`);
    return false;
  };

  const run = async (cmd: Command) => {
    switch (cmd) {
      case "preview": openTab(url); return;
      case "copy": await copyLink("Client link copied."); return;
      case "resend": await copyLink("Link copied. Paste it in a text or email to resend."); return;
      case "followUp":
      case "inbox": if (ghl) openTab(ghl); return;
      case "editResend": draft.startEdit(); return;
      case "send":
        await sending.run(undefined, async () => {
          if (!(await savedFirst())) return;
          status("sent", markSentSuccessMessage({ squareSource: !!quote.square_source, clientEmail: quote.client_email, emailSwitch }));
        });
        return;
      case "markSent":
        await sending.run(undefined, async () => {
          if (await savedFirst()) status("sent", "Marked sent. Nothing went out from the app.", { manualShare: true });
        });
        return;
      case "copySend":
        await sending.run(undefined, async () => {
          if (!(await savedFirst())) return;
          if (await copyText(url)) status("sent", "Link copied and marked sent. Paste it in your message.", { manualShare: true });
          else toast(`Select and copy: ${url}`);
        });
        return;
      case "markAccepted": status("accepted", "Marked accepted. Payments open below."); return;
      case "markDeclined": status("declined", "Marked declined. The event stage updates on its own."); return;
      case "backToDraft":
        // From accepted the server re-checks that nothing is paid (Edge case 14).
        status("draft", "Back to draft. Edit it, then send again.", quote.status === "accepted" ? { onlyIfUnpaid: true } : {});
        return;
      case "reopenQuote": status("sent", "Quote re-opened as sent."); return;
      case "reopenEvent": toggleLost.mutate({ id, is_lost: false }); return;
      case "duplicate": duplicate.mutate(quote, { onSuccess: () => onOpenChange(false) }); return;
      case "pdf": openTab(`/quote/${id}?print=true`); return;
      case "saveTemplate": openTemplate(); return;
      case "viewSquare": if (quote.square_invoice_id) openTab(`https://squareup.com/dashboard/invoices/${quote.square_invoice_id}`); return;
      case "resync": await square.resync(); return;
      case "deleteDraft": askDelete(); return;
      case "collect": setPayMode("collect"); return;
      case "record": setPayMode("record"); return;
      case "delete": return;
    }
  };
  const sendBusy = sending.busy || draft.saving || updateStatus.isPending;
  const busy = {
    duplicate: duplicate.isPending, resync: square.resyncing, reopenEvent: toggleLost.isPending,
    send: sendBusy, markSent: sendBusy, copySend: sendBusy,
  };
  return { run: (cmd: Command) => void run(cmd), busy };
}

export function QuoteDrawer({ quote, onOpenChange, onContactClick, onDeleted }: QuoteDrawerProps) {
  const formId = useId();
  const contentRef = useRef<HTMLDivElement>(null);
  const data = useQuoteDrawerData(quote);
  const draft = useQuoteDrawerDraft(quote, { defaultGratuityPct: data.defaultGratuityPct, defaultDepositPct: data.defaultDepositPct });
  const details = useEventDetailsDraft({ event: data.event, eventError: data.eventError, quote, saveQuote: draft.save, quoteSaving: draft.saving });
  const [detailsEditing, setDetailsEditing] = useState(false);
  const [closeAsk, setCloseAsk] = useState(false);
  const [closing, setClosing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleteAsk, setDeleteAsk] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [payMode, setPayMode] = useState<PayMode>(null);
  const dragGuard = useDragEscapeGuard();
  const { deleteDraft, deleting } = useDeleteDraft(quote, onDeleted);
  // Anything unsaved: the quote's edits or the Edit details form.
  const dirty = draft.dirty || details.dirty;
  const ghl = ghlContactHref(quote.contact?.ghl_location_id, quote.contact?.ghl_contact_id);
  const commands = useDrawerCommands({
    quote, draft, ghl, emailSwitch: emailSwitchFrom(data.settingsQuery), onOpenChange,
    openTemplate: () => setTemplateOpen(true), askDelete: () => setDeleteAsk(true), setPayMode,
  });

  // On phones the whole drawer scrolls (header included, so the items get the screen), so a
  // confirm that opens in the header has to be scrolled back into view.
  useEffect(() => {
    if (closeAsk) contentRef.current?.querySelector("[data-unsaved-bar]")?.scrollIntoView?.({ block: "nearest" });
  }, [closeAsk]);
  useEffect(() => {
    if (deleteAsk) contentRef.current?.scrollTo?.({ top: 0 });
  }, [deleteAsk]);
  // The footer's Save or Discard changes can leave nothing unsaved while the bar shows.
  useEffect(() => {
    if (!dirty) setCloseAsk(false);
  }, [dirty]);

  /** Every way out (×, Close, scrim, Esc, leaving links) asks first while anything is unsaved. */
  const requestClose = (): boolean => {
    if (dirty) {
      setCloseAsk(true);
      setMenuOpen(false);
      return false;
    }
    onOpenChange(false);
    return true;
  };

  // One Esc path: Radix calls onOpenChange(false) unless this prevents it.
  const onEsc = (e: KeyboardEvent) => {
    dragGuard.onEscapeKeyDown(e);
    if (e.defaultPrevented) return;
    if (closeAsk) {
      e.preventDefault();
      setCloseAsk(false);
    } else if (search) {
      e.preventDefault();
      setSearch("");
    }
  };

  const discardAndClose = () => {
    draft.discard();
    details.reset();
    setCloseAsk(false);
    onOpenChange(false);
    toast("Changes discarded.");
  };

  // Quote first, then details; closes only when every dirty save returned true.
  const saveAndClose = async () => {
    setClosing(true);
    try {
      if (draft.dirty && !(await draft.save())) return;
      if (details.dirty && !(await details.save())) return;
    } finally {
      setClosing(false);
    }
    onOpenChange(false);
    toast.success("Saved.");
  };

  // Open event → leaves the page, so it asks first too.
  const onOpenEvent = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!dirty) return;
    e.preventDefault();
    requestClose();
  };
  const contactId = quote.contact_id;
  const onClientClick = onContactClick && contactId
    ? () => void (requestClose() && setTimeout(() => onContactClick(contactId), 200))
    : undefined;

  const itemCount = draft.fields.lines.length;
  const savedItems = quote.line_items?.length ?? 0;
  const summary = summarizePayments({ total: quote.total, amountPaid: quote.amount_paid, pendingAmount: quote.pending_etransfer_amount });
  const step = resolveNextStep({ quote, itemCount, total: draft.totals.total, summary, responses: data.responses, dirty });
  const nextStep = { ...step, buttons: step.buttons.map((b) => (b.id === "followUp" || b.id === "inbox") && !ghl ? { ...b, disabled: true } : b) };
  const deleteBlocked = data.deletable === false || (Number(quote.amount_paid) || 0) > 0;
  const docLabel = getDocumentLabel(quote.status, quote.payment_status);

  return (
    <Sheet open onOpenChange={(o) => { if (!o) requestClose(); }}>
      <SheetContent
        ref={contentRef}
        hideClose
        side="right"
        aria-describedby={undefined}
        onEscapeKeyDown={onEsc}
        className="flex w-full max-w-none flex-col gap-0 bg-background p-0 sm:max-w-none min-[761px]:w-[760px] max-[760px]:overflow-y-auto max-[760px]:[&_input]:text-base max-[760px]:[&_select]:text-base max-[760px]:[&_textarea]:text-base"
      >
        <QuoteDrawerHeader
          quote={quote}
          summary={summary}
          docLabel={docLabel}
          eventNumber={data.event?.event_number ?? null}
          menu={{
            entries: menuEntries({ quote, itemCount: savedItems, deletable: data.deletable, dirty }),
            onSelect: commands.run, open: menuOpen, onOpenChange: setMenuOpen, busy: { ...commands.busy, deleteDraft: deleting },
          }}
          onClose={requestClose}
          onOpenEvent={onOpenEvent}
          onClientClick={onClientClick}
          closeAsk={closeAsk ? { onKeep: () => setCloseAsk(false), onDiscard: discardAndClose, onSave: () => void saveAndClose(), saving: closing || draft.saving || details.saving } : null}
          deleteAsk={deleteAsk ? {
            text: `Delete draft ${quote.quote_number}${savedItems ? ` and its ${savedItems} item${savedItems > 1 ? "s" : ""}` : ""}? This can't be undone.`,
            onKeep: () => setDeleteAsk(false),
            onDelete: async () => {
              if (!(await deleteDraft({ itemCount: savedItems, total: Number(quote.total) || 0 }))) setDeleteAsk(false);
            },
            busy: deleting,
          } : null}
          nextStep={nextStep}
          onNextStep={commands.run}
          nextStepBusy={commands.busy}
        />
        <div data-drawer-body className="grid flex-1 content-start gap-3 overflow-y-auto px-5 py-4 max-[760px]:flex-none max-[760px]:overflow-visible max-[760px]:px-4">
          {quote.square_source && (
            <SquareBar onView={() => commands.run("viewSquare")} onResync={() => commands.run("resync")} resyncing={commands.busy.resync} resyncDisabled={dirty} />
          )}
          <form id={formId} className="grid gap-3" onSubmit={(e) => { e.preventDefault(); void draft.save(); }}>
            <ItemsSection quote={quote} draft={draft} dietaryLabels={data.dietaryLabels} search={search}
              onSearchChange={setSearch} onDraggingChange={dragGuard.onDraggingChange} />
            <TotalsSection quote={quote} draft={draft} docLabel={docLabel} />
          </form>
          <PaymentsSection
            quote={quote}
            summary={summary}
            payments={data.payments}
            paymentsError={data.paymentsError}
            depositPct={quote.deposit_percent ?? data.defaultDepositPct ?? 20}
            etransferEmail={data.etransferEmail}
            mode={payMode}
            onModeChange={setPayMode}
          />
          <EventClientSection quote={quote} details={details} editing={detailsEditing} onEditingChange={setDetailsEditing} quoteDirty={draft.dirty} />
          <CrewSection quote={quote} />
          <NotesSection draft={draft} first={firstName(quote.client_name)} />
          <ActivitySection quote={quote} lastMessage={data.lastMessage} responses={data.responses} payments={data.payments} />
        </div>
        <DrawerFooter
          editing={draft.editing}
          dirty={draft.dirty}
          status={quote.status}
          formId={formId}
          saving={draft.saving}
          onDeleteDraft={() => setDeleteAsk(true)}
          deleteDisabled={deleteBlocked || deleting}
          onDiscard={draft.discard}
          onDone={draft.discard}
          onClose={requestClose}
        />
        {templateOpen && <SaveTemplateDialog quote={quote} open onOpenChange={setTemplateOpen} />}
      </SheetContent>
    </Sheet>
  );
}
